import { Box, Typography } from "@material-ui/core";
import React, { useContext, useEffect } from "react";
import { PackagesSearchControl } from "./components/PackagesSearchControl";
import "./styles.scss";
import { PackagesSearchConnectorProps } from "./container";
import { RouteComponentProps } from "react-router-dom";
import {
  CallState,
  PackagesEntryTypeEnum,
  VIEWED_PACKAGES_SEARCH,
} from "redmond";
import { Skeleton } from "@material-ui/lab";
import {
  SUBTITLE,
  TITLE,
  INFO_MODAL_CTA_TEXT,
  CONFIRM_TRAVELER_DETAILS,
} from "./textConstants";
import { BannerSeverity, NotificationBanner, useDeviceTypes } from "halifax";
import { MobilePackagesSearchControl, PackagesInfoModal } from "./components";
import { ButtonWrap, Icon, IconName } from "halifax";
import { PORTAL_TITLE, SEARCH_PACKAGE_TITLE } from "../../lang/textConstants";
import { listPaymentMethods } from "../availability/actions/actions";
import { ClientContext } from "../../App";
import {
  IPackagesParsedQuery,
  parseQueryString,
} from "./utils/parseQueryString";
import { fetchFlightLocation } from "../../api/v0/search/fetchFlightLocation";
import { fetchHotelLocation } from "../../api/v0/search/fetchHotelLocation";
import { trackEvent } from "../../api/v0/trackEvent";

interface IPackagesSearchProps
  extends PackagesSearchConnectorProps,
    RouteComponentProps {}

export const PackagesSearch = ({
  largestValueAccount,
  fetchRewardsAccountsCallState,
  fetchRewardsAccounts,
  resetPackagesAvailabilityCallState,
  setDepartureDate,
  setReturnDate,
  setOrigin,
  setDestination,
  setFareclassOptionFilter,
  setStopsOption,
  history,
  setTravelers,
}: IPackagesSearchProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { sessionInfo } = useContext(ClientContext);

  const [packagesInfoModalOpen, setPackagesInfoModalOpen] =
    React.useState(false);

  const getSubtitleToDisplay = () => {
    if (
      (fetchRewardsAccountsCallState === CallState.Success ||
        fetchRewardsAccountsCallState === CallState.Failed) &&
      largestValueAccount
    ) {
      return SUBTITLE(largestValueAccount);
    } else {
      return <Skeleton className="subtitle-loading" />;
    }
  };

  const parsedQueryString = parseQueryString(history);

  const populateFieldsFromQueryString = async ({
    fromDate,
    untilDate,
    origin,
    destination,
    stopsOption,
    fareClass,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
  }: IPackagesParsedQuery) => {
    if (fromDate) {
      setDepartureDate(fromDate);
    }

    if (untilDate) {
      setReturnDate(untilDate);
    }

    if (origin) {
      const originLocation = await fetchFlightLocation(origin);
      setOrigin(originLocation);
    }

    if (destination) {
      const destinationLocation = await fetchHotelLocation(destination);
      setDestination(destinationLocation);
    }

    if (stopsOption) {
      setStopsOption(stopsOption);
    }

    if (fareClass) {
      setFareclassOptionFilter({
        basic: false,
        standard: false,
        enhanced: false,
        premium: false,
        luxury: false,
        [fareClass]: true,
      });
    }
    if (
      adultsCount ||
      childrenCount ||
      infantsInSeatCount ||
      infantsOnLapCount
    ) {
      // hardcoding to be age of 1 for infants and 11 for children per the ticket [CMKT-4767]
      const infantsArr = [
        ...(infantsInSeatCount
          ? new Array(infantsInSeatCount ?? 0).fill({ age: 1, inSeat: true })
          : []),
        ...(infantsOnLapCount
          ? new Array(infantsOnLapCount ?? 0).fill({ age: 1, inSeat: false })
          : []),
      ];
      setTravelers({
        adultsCount: adultsCount,
        children: childrenCount ? new Array(childrenCount).fill(11) : [],
        infants: infantsArr,
      });
    }
  };

  useEffect(() => {
    document.title = SEARCH_PACKAGE_TITLE;
    fetchRewardsAccounts(false, sessionInfo);
    listPaymentMethods();

    setTimeout(() => window.scrollTo(0, 0), 0);

    resetPackagesAvailabilityCallState();

    if (parsedQueryString) {
      (async () => {
        await populateFieldsFromQueryString(parsedQueryString);
      })();
    }

    trackEvent({
      eventName: VIEWED_PACKAGES_SEARCH,
      properties: {
        ...(!!parsedQueryString?.entryPoint && {
          entry_point: parsedQueryString.entryPoint as PackagesEntryTypeEnum,
        }),
      },
    });

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  if (matchesMobile)
    return (
      <Box className="mobile-packages-search-root">
        <MobilePackagesSearchControl />
      </Box>
    );

  return (
    <Box className="packages-search-root">
      <Box className="packages-search-container-desktop">
        <Box className="packages-search-titles-and-fields-wrapper">
          <Typography variant="h1" className="search-title">
            {TITLE}
          </Typography>
          <Typography variant="h2" className="search-subtitle">
            {getSubtitleToDisplay()}
          </Typography>

          <PackagesSearchControl />
          {(parsedQueryString?.childrenCount ||
            parsedQueryString?.infantsInSeatCount ||
            parsedQueryString?.infantsOnLapCount) &&
          parsedQueryString.entryPoint ? (
            <NotificationBanner
              className="confirm-travelers-banner"
              label={CONFIRM_TRAVELER_DETAILS}
              severity={BannerSeverity.WARNING}
            />
          ) : undefined}
          <ButtonWrap
            className="packages-info-cta"
            onClick={() => setPackagesInfoModalOpen(true)}
          >
            <Box className="new-tag">New</Box>
            <Typography className="packages-info-cta-text">
              {INFO_MODAL_CTA_TEXT}
            </Typography>
            <Icon name={IconName.InfoCircle} />
          </ButtonWrap>

          <PackagesInfoModal
            open={packagesInfoModalOpen}
            setOpen={setPackagesInfoModalOpen}
          />
        </Box>
      </Box>
    </Box>
  );
};
